import { IDocumentBinder } from 'shared/domain/document/documentBinder';
import { DocumentUrlsModel } from 'shared/domain/document/documentUrlsModel';
import {
  SyncStatus,
  isSyncStatusSuccess,
} from 'shared/domain/entitySyncStatus/syncStatus';
import { ImageConvertStatus } from 'shared/domain/imageConvertStatus/imageConvertStatus';
import { DocumentOnView } from 'presentation/document/documentOnView';
import React, { MutableRefObject } from 'react';
import { FileInfo } from 'setup/types/core';
import { HashMap } from 'shared/types/commonView';
import { isBlobUrl } from 'shared/utils/blobUrl';
import type { DisplayedFile } from '../gallery/filesStore';
import { MainImage } from '../mainImage';
import { ImageRotation } from 'shared/dtos/out/document';

export type UploaderFile<T = object> = FileType & T;

export const FILE_STATE_KIND = {
  SAVED: 'saved',
  UNSAVED: 'unsaved',
} as const;

export type FileStateKind =
  (typeof FILE_STATE_KIND)[keyof typeof FILE_STATE_KIND];

export type SavedFileType = FileType & {
  fileStateKind: typeof FILE_STATE_KIND.SAVED;
  signedRequest?: DocumentUrlsModel;
};

export type UnsavedFileType = FileType & {
  fileStateKind: typeof FILE_STATE_KIND.UNSAVED;
  localId?: undefined;
  signedRequest?: undefined;
};

export type StatefulFileType = SavedFileType | UnsavedFileType;

export function inferFileStateKind(file: {
  fileStateKind?: FileStateKind;
  localId?: number;
  signedRequest?: DocumentUrlsModel;
}): FileStateKind {
  if (file.fileStateKind) {
    return file.fileStateKind;
  }

  return file.localId === undefined && !file.signedRequest
    ? FILE_STATE_KIND.UNSAVED
    : FILE_STATE_KIND.SAVED;
}

export function isUnsavedFileType(file: {
  fileStateKind?: FileStateKind;
  localId?: number;
  signedRequest?: DocumentUrlsModel;
}): file is UnsavedFileType {
  return inferFileStateKind(file) === FILE_STATE_KIND.UNSAVED;
}

export function isSavedFileType(file: {
  fileStateKind?: FileStateKind;
  localId?: number;
  signedRequest?: DocumentUrlsModel;
}): file is SavedFileType {
  return inferFileStateKind(file) === FILE_STATE_KIND.SAVED;
}

export function shouldIncludeDownloadSrcInCleanup(file: {
  fileStateKind?: FileStateKind;
  localId?: number;
  _id?: string;
  src?: string;
  downloadSrc?: string;
  thumbnail?: string;
  signedRequest?: DocumentUrlsModel;
  syncStatus?: SyncStatus;
}): boolean {
  const hasRemoteId =
    typeof file._id === 'string' && file._id.length > 0;
  const hasSignedRequest = Boolean(file.signedRequest);
  const hasBlobDownloadSrc = isBlobUrl(file.downloadSrc);

  if (hasBlobDownloadSrc) {
    // Keep local blob until upload is fully completed; queue retries still need it.
    if (!isSyncStatusSuccess(file.syncStatus)) {
      return false;
    }

    // Even after success, keep blob if remote source has not been fully resolved.
    if (!hasRemoteId || !hasSignedRequest) {
      return false;
    }
  }

  return file.localId !== undefined || !isUnsavedFileType(file);
}

type EditPlace<T = object> = 'deleted' | 'description' | 'imageRotation' | keyof T;
type EditValue = string | number | boolean | null | undefined;

interface GraphicUploaderClasses { buttonContainer?: string }

export const GRAPHIC_UPLOADER_GALLERY_MODE = {
  DEFAULT: 'default',
  ISSUE_ATTACHMENTS: 'issueAttachments',
  ISSUE_LINKED_DOCUMENTATION: 'issuelinkedDocumentation',
} as const;

export type GraphicUploaderGalleryMode =
  (typeof GRAPHIC_UPLOADER_GALLERY_MODE)[keyof typeof GRAPHIC_UPLOADER_GALLERY_MODE];

export type GUProps = Pick<
  GU,
  | 'filesStore'
  | 'addFile'
  | 'editFile'
  | 'removeFile'
  | 'rotateFile'
  | 'config'
  | 'drawings'
  | 'addDrawing'
> & {
  classes?: GraphicUploaderClasses;
  forwardedRef?: MutableRefObject<ContextType | undefined>;
};

export interface CONFIG_TYPE {
  baseUrl: string;
  documentScopeKey?: string;
  max_file_size?: number;
  file_types?: string[];
  thumbnailSize?: number;
  atomicChanges?: boolean;
  dataQaPrefix: string;
  actions?: {
    upload?: Function;
    edit?: Function;
    rotate: Function;
    remove?: Function;
    uploadDrawing?: Function;
  };
  previewOnly?: boolean;
  internalEntityBaseUrl?: Function;
  // TODO: make it required in localGraphicUploader; PT-3769
  documentBinderFactory?: (baseUrl?: string) => IDocumentBinder;
  sourceResolver?: (
    localId: number
  ) => Promise<{ documentUrls: DocumentUrlsModel }>;
  filePrefix?: string;
  autoOpenCheck?: (
    baseUrl: string,
    file: FileType,
    prefix?: string
  ) => boolean;
  mainImageHandlerRef?: MutableRefObject<MainImage>;
  hideAddFiles?: boolean;
  addMenuActions?: boolean;
  showAddTileOnly?: boolean;
  fetchShareableUrl?: (documentId: string) => Promise<string>;
  /** High-level gallery mode that controls visible actions and edit capabilities. */
  galleryMode?: GraphicUploaderGalleryMode;
  /** Whose attachments the drag-over snackbar names. Defaults to the issue. */
  attachmentDropTarget?: 'issue' | 'inspection';
  /** Optional flag used by UI sizing logic to distinguish true empty state from loading. */
  hasFiles?: boolean;
  /** True while linked documentation previews/details are still being fetched. */
  isLoadingFiles?: boolean;
}

type SETTINGS = Partial<CONFIG_TYPE>;

type FileAPI = DocumentOnView;

export interface FileType {
  file?: File;
  src?: string;
  thumbnail?: string;
  size?: number;
  title: string;
  description?: string | null;
  isExportAllowed?: boolean;
  imageRotation: ImageRotation;
  type: string;
  deleted?: boolean;
  _id?: string;
  data?: Partial<FileInfo>;
  error?: string;
  index?: number;
  signedRequest?: DocumentUrlsModel;
  localId?: number;
  downloadSrc?: string | undefined;
  drawing?: DrawingType;
  imageConvertStatus?: ImageConvertStatus;
  pdfPageCount?: number;
  drawingSyncStatus?: SyncStatus;
  modifiedAt?: string;
  versionId?: string;
  fileStateKind?: FileStateKind;
  documentationId?: string;
  /** A line under the title on the tile, e.g. what a linked documentation shows. */
  caption?: string;
  /** A warning above the file in the gallery preview, e.g. that a linked version is outdated. */
  previewNotice?: string;
  /**
   * The documentation's current version number shown next to the title in the
   * preview (PT-7117); `null`: it has no current version; `undefined`: not shown.
   */
  currentVersionNumber?: number | null;
  /** The version the file is linked to, pinned: "go to documentation" opens that version, not the current one. */
  pinnedVersionId?: string;
  /** Number of the shown version when it is withdrawn: the preview dims it under a notice. */
  withdrawnVersionNumber?: number;
}

export interface DefinedDrawing {
  data: string;
  blob: any;
  imageUrl: string;
  mergedImageUrl: string;
  thumbnailMergedImageUrl?: string;
  drawingImageUrl?: string;
  clear: boolean;
}
interface ClearDrawing {
  data?: undefined;
  blob?: undefined;
  imageUrl?: undefined;
  mergedImageUrl?: undefined;
  thumbnailMergedImageUrl?: undefined;
  drawingImageUrl?: undefined;
  clear: true;
}
interface AnyDrawing {
  data?: string;
  blob?: any;
  imageUrl?: string;
  mergedImageUrl?: string;
  thumbnailMergedImageUrl?: string;
  drawingImageUrl?: string;
  clear?: boolean;
}

export type DrawingType =
  | DefinedDrawing
  | ClearDrawing
  | AnyDrawing
  | undefined;

export type Files = FileType[];

type Drawings = DrawingType[];

interface UploadFilesOptions {
  rethrow?: boolean;
}

interface FilesStore {
  get: () => DisplayedFile[];
  set: (f: UploaderFile[]) => void;
  subscribe: (callback: () => void) => () => void;
}
export interface GU {
  filesStore: FilesStore;
  addFile: (file: File[]) => void;
  editFile: (id: number, key: EditPlace, value: EditValue) => void;
  removeFile: (id: number) => void;
  rotateFile: (id: number, value: ImageRotation) => void;
  uploadFiles: (
    baseUrl: string,
    onDone: ((params?: any) => any) | undefined,
    options?: UploadFilesOptions
  ) => any;
  drawings: Drawings;
  addDrawing: (drawing: DrawingType, index: number) => void;
  loading: boolean;
  config: CONFIG_TYPE;
  changeSet: ChangeSet;
  executeChangeset: () => Promise<any>[];
}

export type UseGraphicUploader = (
  initialFiles?: FileAPI[],
  settings?: SETTINGS,
  alwaysReinit?: true
) => GU;

export type Event = React.ChangeEvent<HTMLInputElement>;

type GallerySetter = (params: { slide: number; open: boolean }) => void;

interface ExtendContext {
  setGallery: GallerySetter;
  changeGallery: (
    place: 'open' | 'slide',
    value: number | boolean
  ) => void;
  gallery: { slide: number; open: boolean };
}

export type ContextType = GUProps & ExtendContext;

export type AtomicCall = (
  config: CONFIG_TYPE,
  actionName: keyof NonNullable<CONFIG_TYPE['actions']>,
  params: any,
  fallback: Function
) => void;

export type Changes = Partial<Record<EditPlace, EditValue>>;
export type ChangeSet = HashMap<Changes>;

export interface FileReadSuccess extends FileReadResult {
  data: {
    extension: string;
    isDrawn?: boolean;
    mergedSrc?: string;
  };
  // TODO: fix DocumentOnView description PT-3773
  description?: string | null | undefined;
  file?: File;
  src: string;
  title: string;
  type: string;
  // Files can have localId undefined when they were not saved to DB yet.
  localId?: number;
  resultType: ResultType.success;
  signedRequest?: DocumentUrlsModel;
  downloadSrc: string | undefined;
  syncStatus: SyncStatus;
  drawingSyncStatus: SyncStatus;
  imageRotation: ImageRotation;
  fileStateKind?: FileStateKind;
}

export interface FileReadError extends FileReadResult {
  error: string;
  resultType: ResultType.error;
}

export enum ResultType {
  success = 'success',
  error = 'error',
}

export interface FileReadResult {
  resultType: ResultType;
}
